import React, {createContext, useContext, useState, useEffect, ReactNode} from 'react'
import {UserRole} from '../utils/permissions'

// User context interface defining current user data and scope
export interface UserContextData {
  // User identity
  userId: string
  email: string
  role: UserRole
  firstName: string
  lastName: string

  // Scope restrictions based on role
  agencyId?: string // For agency/MSP admins - limits to their agency
  companyIds?: string[] // For company admins - limits to their companies
  assignedAgencyIds?: string[] // For MSP admins - agencies they can manage
  assignedCompanyIds?: string[] // For agency admins - companies they can manage

  // Feature flags and permissions
  permissions: string[]
  isEOREnabled?: boolean // For MSP/Agency admins

  // UI preferences
  theme?: 'light' | 'dark'
  language?: string
}

interface UserContextState {
  user: UserContextData | null
  isLoading: boolean
  error: string | null

  // Methods to update user context
  setUser: (user: UserContextData) => void
  updatePermissions: (permissions: string[]) => void
  logout: () => void

  // Scope checking utilities
  canAccessAgency: (agencyId: string) => boolean
  canAccessCompany: (companyId: string) => boolean
  getAccessibleCompanies: () => string[]
  getAccessibleAgencies: () => string[]

  // Role-specific getters
  isRestrictedToOwnAgency: () => boolean
  isRestrictedToAssignedCompanies: () => boolean
}

const UserContext = createContext<UserContextState | undefined>(undefined)

interface UserContextProviderProps {
  children: ReactNode
}

export const UserContextProvider: React.FC<UserContextProviderProps> = ({children}) => {
  const [user, setUserState] = useState<UserContextData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Initialize user context from localStorage or API
  useEffect(() => {
    const initializeUser = async () => {
      try {
        setIsLoading(true)

        // Try to load user from localStorage first
        const storedUser = localStorage.getItem('userContext')
        if (storedUser) {
          const parsedUser = JSON.parse(storedUser) as UserContextData
          setUserState(parsedUser)
        }

        // In real app, fetch fresh user data from API
        // const userData = await fetchCurrentUser()
        // setUserState(userData)

        setError(null)
      } catch (err) {
        setError('Failed to initialize user context')
        console.error('User context initialization error:', err)
      } finally {
        setIsLoading(false)
      }
    }

    initializeUser()
  }, [])

  // Persist user data to localStorage when it changes
  useEffect(() => {
    if (user) {
      localStorage.setItem('userContext', JSON.stringify(user))
    } else {
      localStorage.removeItem('userContext')
    }
  }, [user])

  const setUser = (userData: UserContextData) => {
    setUserState(userData)
    setError(null)
  }

  const updatePermissions = (permissions: string[]) => {
    if (user) {
      setUserState({...user, permissions})
    }
  }

  const logout = () => {
    setUserState(null)
    localStorage.removeItem('userContext')
    setError(null)
  }

  // Scope checking utilities
  const canAccessAgency = (agencyId: string): boolean => {
    if (!user) return false

    switch (user.role) {
      case UserRole.PLATFORM_ADMIN:
        return true // Platform admin can access all agencies

      case UserRole.MSP_ADMIN:
        return user.assignedAgencyIds?.includes(agencyId) || false

      case UserRole.AGENCY_ADMIN:
        return user.agencyId === agencyId // Only their own agency

      default:
        return false
    }
  }

  const canAccessCompany = (companyId: string): boolean => {
    if (!user) return false

    switch (user.role) {
      case UserRole.PLATFORM_ADMIN:
        return true // Platform admin can access all companies

      case UserRole.MSP_ADMIN:
        return user.assignedCompanyIds?.includes(companyId) || false

      case UserRole.AGENCY_ADMIN:
        return user.assignedCompanyIds?.includes(companyId) || false

      case UserRole.COMPANY_ADMIN:
        return user.companyIds?.includes(companyId) || false

      default:
        return false
    }
  }

  const getAccessibleCompanies = (): string[] => {
    if (!user) return []

    switch (user.role) {
      case UserRole.PLATFORM_ADMIN:
        return ['*'] // Represents all companies

      case UserRole.MSP_ADMIN:
      case UserRole.AGENCY_ADMIN:
        return user.assignedCompanyIds || []

      case UserRole.COMPANY_ADMIN:
        return user.companyIds || []

      default:
        return []
    }
  }

  const getAccessibleAgencies = (): string[] => {
    if (!user) return []

    switch (user.role) {
      case UserRole.PLATFORM_ADMIN:
        return ['*'] // Represents all agencies

      case UserRole.MSP_ADMIN:
        return user.assignedAgencyIds || []

      case UserRole.AGENCY_ADMIN:
        return user.agencyId ? [user.agencyId] : []

      default:
        return []
    }
  }

  const isRestrictedToOwnAgency = (): boolean => {
    return user?.role === UserRole.AGENCY_ADMIN
  }

  const isRestrictedToAssignedCompanies = (): boolean => {
    return user?.role === UserRole.AGENCY_ADMIN || user?.role === UserRole.MSP_ADMIN
  }

  const contextValue: UserContextState = {
    user,
    isLoading,
    error,
    setUser,
    updatePermissions,
    logout,
    canAccessAgency,
    canAccessCompany,
    getAccessibleCompanies,
    getAccessibleAgencies,
    isRestrictedToOwnAgency,
    isRestrictedToAssignedCompanies,
  }

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
}

// Custom hook to use the user context
export const useUserContext = (): UserContextState => {
  const context = useContext(UserContext)
  if (context === undefined) {
    throw new Error('useUserContext must be used within a UserContextProvider')
  }
  return context
}

// Mock data for development - remove in production
export const createMockAgencyAdmin = (agencyId: string): UserContextData => ({
  userId: 'user-123',
  email: 'admin@jsnorthamerica.com',
  role: UserRole.AGENCY_ADMIN,
  firstName: 'John',
  lastName: 'Doe',
  agencyId: agencyId,
  assignedCompanyIds: ['company-1', 'company-2', 'company-3'], // Companies assigned to this agency
  permissions: [
    'agency:profile:read',
    'agency:profile:edit',
    'agency:catalogs:read',
    'agency:users:read',
    'agency:orders:read',
    'agency:documents:read',
    'company:profile:read',
    'company:profile:edit',
    'company:positions:read',
    'company:positions:create',
    'company:users:read',
    'company:orders:read',
    'company:documents:read',
  ],
  theme: 'light',
  language: 'en',
})

export const createMockMSPAdmin = (mspId: string): UserContextData => ({
  userId: 'user-456',
  email: 'msp@jscarson.com',
  role: UserRole.MSP_ADMIN,
  firstName: 'Jane',
  lastName: 'Smith',
  agencyId: mspId,
  assignedAgencyIds: ['agency-1', 'agency-2', 'agency-3'], // Agencies under this MSP
  assignedCompanyIds: ['company-1', 'company-2', 'company-3', 'company-4', 'company-5'],
  permissions: [
    'agency:*',
    'company:*',
    'msp:banking:read',
    'msp:banking:edit',
    'msp:tax:read',
    'msp:tax:edit',
  ],
  isEOREnabled: true,
  theme: 'light',
  language: 'en',
})

export const createMockCompanyAdmin = (companyId: string): UserContextData => ({
  userId: 'user-789',
  email: 'admin@tacosElgavilan.com',
  role: UserRole.COMPANY_ADMIN,
  firstName: 'Carlos',
  lastName: 'Rodriguez',
  companyIds: [companyId],
  permissions: [
    'company:profile:read',
    'company:profile:edit',
    'company:positions:read',
    'company:positions:create',
    'company:users:read',
    'company:users:edit',
    'company:orders:read',
    'company:documents:read',
  ],
  theme: 'light',
  language: 'en',
})
